/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@material-ui/core/Button';
import { find } from 'lodash-es';
import moment from 'moment-timezone';
import {
  BusyOverlay,
  VisitContents,
  SuggestBox,
  ReactDatePicker,
  ReactDateTimePicker,
} from '@mummssoftware/common-ui';
import {
  utcToTz,
  momentToDate,
  dateToMoment,
} from '@mummssoftware/utils/dates';
import { emptyOption, Deceased } from '@mummssoftware/utils/constants';
import Checkbox from '@material-ui/core/Checkbox';
import {
  GlobalContext,
  attemptedVisit,
} from '@mummssoftware/global-context/GlobalContext';
import {
  TaskGlobalContext,
  PNGlobalContext,
  VisitsGlobalContext,
  SET_PROVIDER_INFO,
  SET_APP_VISIT_DETAILS,
  SET_REFRESH_VISIT,
  HopeGlobalContext,
} from '@mummssoftware/global-context';
import {
  getUsersGeolocation,
  handleGeolocationError,
} from '@mummssoftware/utils/web';
import { useStyles } from '@mummssoftware/common-ui/VisitAttributeSelector';
import { getVisitByPersonAndVisitId } from '@mummssoftware/utils/pimReader';
import { getDurationMinutesFromVisitSources } from '@mummssoftware/utils/visitUtils';
import {
  formatUTCLocalMMDDYY,
  formatPimDate,
} from '@mummssoftware/utils/formatters';
import {
  getCurrentMilestone,
  getHopeDocName,
  siaRequiresSfv,
  updateVisitSFVRequired,
} from '@mummssoftware/utils/hope';
import {
  GET_TIME,
  SET_VISIT_TITLE,
  SET_VISIT_ALERT,
  RESET_HCPCS,
  STOP_VISIT,
  SHOW_VISIT_LOADER,
  SHOW_CONFIRM_VISIT,
  Completed,
  IPU,
  SET_ACTING_AS_ROLE,
  SET_SAVED_VISIT,
  SET_OPENED_FROM_VISIT_LOG_REOPEN,
} from '../Context/constants';
import { DeleteVisitBtn } from './DeleteVisitBtn';
import { ConfirmVisitDialog } from './ConfirmVisitDialog';
import { VisitContext } from '../Context/contexts/VisitContext';
import { HCPCSContext } from '../Context/contexts/HCPCSContext';
import { visitHasSavedHCPCS } from '../HCPCS/LoadSavedHCPCS';
import { EditTime } from '../Timer';
import { Fees } from './Fees';
import { Context } from '../../context';
import {
  RecordVisitDiv,
  RecordVisitIntroGrid,
  CloseVisitButtonsDiv,
  RecordVisitSummaryLayout,
  RecordVisitSummaryColumn,
  SubmitVisitButton,
  VisitWarning,
  VisitInterfaceDiv,
  TextDiv,
  SmallDiv,
} from '../../components';
import defaults from '../../translations/en.json';
import { FaceToFace } from './FaceToFace';
import { NurseNotificationCheckbox } from './NurseNotificationCheckbox';
import { HopeValidationModal } from './HopeValidationModal';
import {
  DurationPrompt,
  HhMm,
  getInitialDuration,
  toMinutes,
} from './DurationPrompt';

type GeolocationHandlerError = Parameters<typeof handleGeolocationError>[0];

export const RecordVisit = () => {
  const intl = useIntl();
  const classes = useStyles();
  const { progressNotesGlobal } = useContext(PNGlobalContext);
  const { patientAllCPPTasks } = useContext(TaskGlobalContext);
  const {
    appVisit,
    numberForms,
    reloadMesures,
    dispatch: dispatchGlobal,
    appVisitDetails,
    levelOfCare,
  } = useContext(GlobalContext);
  const { providerInfo, dispatchVG } = useContext(VisitsGlobalContext);
  const {
    isHopeVisit,
    canSubmitHopeVisit,
    updateHopeVisitStatus,
    refreshHopeData,
    runHopeRecordVisitSequence,
    availableHOPEActions,
    visitRequiresSFV,
    isOtherInProgHopeVisit,
    hopeVisitAttributesSaved,
    hopeMissingDocumentation,
    hopeMissingSIA,
    hopeValidationPending,
    SIAData,
  } = useContext(HopeGlobalContext);
  const {
    patient,
    currentUser,
    agency,
    keycloak,
    visitAttributes,
  } = useContext(Context);

  const {
    savedVisit,
    closePatientVisit,
    updatePatientVisit,
    dispatchVisit,
    travelMileage,
    transitFees,
    travelDuration,
    tollsFee,
    ccTime,
    visitError,
    IPUVisit,
    editFaceToFace,
    canDeleteOpenVisit,
    stopVisit,
    roleGroups,
    visitTypeOptions,
    is30MinAgency,
    visitEpisode,
    openedFromVisitLogReopen,
    showConfirmVisit,
  } = useContext(VisitContext);

  const siteTimezone =
    (currentUser && currentUser.siteTimezone) || moment.tz.guess();

  /**
   * Record-visit intro bar: values come from `savedVisit` in context (initially from PIM after
   * StartVisit). Intro edits patch `savedVisit` locally; PIM is updated on Submit Visit only.
   */
  /** HCPCS lives on live visit as `physicianServices`; pimreader details use `physicianservices`. */
  const visitContentsDto = useMemo(() => {
    if (!appVisitDetails) return appVisitDetails;
    const fromDetails =
      (appVisitDetails as any).physicianservices ??
      (appVisitDetails as any).physicianServices;
    if (fromDetails) return appVisitDetails;
    const fromVisit =
      (appVisit as any)?.physicianServices ??
      (appVisit as any)?.physicianservices ??
      (savedVisit as any)?.physicianServices ??
      (savedVisit as any)?.physicianservices;
    if (!fromVisit) return appVisitDetails;
    return { ...appVisitDetails, physicianServices: fromVisit };
  }, [appVisitDetails, appVisit, savedVisit]);

  const recordSummaryRoleOption = useMemo(() => {
    if (!savedVisit?.careProviderRoleGroup || !roleGroups?.length) {
      return emptyOption;
    }
    return (
      find(
        roleGroups,
        (r: any) => r.label === savedVisit.careProviderRoleGroup,
      ) ||
      find(
        roleGroups,
        (r: any) => r.rolename === savedVisit.careProviderRoleGroup,
      ) || {
        ...emptyOption,
        label: savedVisit.careProviderRoleGroup,
        value: savedVisit.careProviderRoleGroup,
      }
    );
  }, [savedVisit?.careProviderRoleGroup, roleGroups]);

  const recordSummaryVisitTypeOption = useMemo(() => {
    if (!savedVisit) {
      return emptyOption;
    }
    const options = visitTypeOptions || [];
    const byVisitType = find(options, { value: savedVisit.visitType });
    if (byVisitType) {
      return byVisitType;
    }
    const displayVt = (savedVisit as mumms.PatientVisit & {
      displayvisittype?: string | null;
    }).displayvisittype;
    if (displayVt) {
      const byDisplay = find(options, { value: displayVt });
      if (byDisplay) {
        return byDisplay;
      }
    }
    const fallbackLabel =
      savedVisit.visitType || displayVt || emptyOption.label;
    return { ...emptyOption, label: fallbackLabel, value: fallbackLabel };
  }, [savedVisit, visitTypeOptions]);

  const recordSummaryVisitDate = useMemo(() => {
    if (!savedVisit?.startDate) {
      return new Date();
    }
    return utcToTz(savedVisit.startDate, siteTimezone);
  }, [savedVisit?.startDate, siteTimezone]);

  /**
   * Min/max calendar bounds mirror VisitIntro: referral vs episode windows, deceased-last-episode
   * rule, and discharge cap — so editing the visit date while recording follows the same rules
   * as picking it before start.
   */
  const isAttemptedVisit = levelOfCare === attemptedVisit;
  const showOnlyDate = IPUVisit || isAttemptedVisit;
  const isPatientDD = patient?.currentStatus === Deceased;
  const referralDate =
    patient && new Date((patient as mumms.patient).referralDate);
  const episodeReferralDate =
    visitEpisode &&
    visitEpisode.utcReferralDate &&
    moment.tz(visitEpisode.utcReferralDate, siteTimezone);
  const episodeDischargeDate =
    visitEpisode &&
    visitEpisode.utcDischargeDate &&
    moment.tz(visitEpisode.utcDischargeDate, siteTimezone);
  const minDate = episodeReferralDate || moment(referralDate);
  const DDrule = isPatientDD && visitEpisode?.isLast;
  const maxDate =
    episodeDischargeDate && !DDrule
      ? episodeDischargeDate?.toDate()
      : new Date();
  const isDateTimePicker = showOnlyDate && !IPUVisit;
  const {
    hcpcsPermission,
    hcpcsCodesMap,
    hcpcsDuration,
    icdCodes,
    dispatchHCPCS,
    showVisitLoader,
    showHCPCS,
  } = useContext(HCPCSContext);
  const [disabled, setDisabled] = useState<boolean>(false);
  /** Blocks duplicate closePatientVisit calls (confirm double-click, overlapping finishVisit). */
  const visitClosingRef = useRef(false);
  /**
   * First-call-wins guard for async checkForVisit (canDeleteOpenVisit may fetch visit details).
   * Prevents a stale second check from calling finishVisit while the confirm dialog is open.
   */
  const checkForVisitInFlightRef = useRef(false);
  const [showDurationPrompt, setShowDurationPrompt] = useState<boolean>(false);
  const [warnF2F, setWarnF2F] = useState<boolean>(false);
  const [isNurseNotified, setNurseNotified] = useState<boolean>(false);
  const [hopeValidationModal, setHopeValidationModal] = useState<{
    blockingErrors: any[];
    hopeDTOProp: string;
  } | null>(null);
  const hopeErrorIntlValues = useMemo(() => {
    const m = getCurrentMilestone(availableHOPEActions);
    const docName = getHopeDocName(m || '');
    return {
      hasNamedDoc: docName ? 'yes' : 'other',
      docName,
    };
  }, [availableHOPEActions]);
  const hopeSubmitBlocked =
    !!isHopeVisit &&
    (canSubmitHopeVisit !== true ||
      !hopeVisitAttributesSaved ||
      !!isOtherInProgHopeVisit);
  const getHopeSubmitAlertText = () => {
    if (hopeValidationPending) {
      return intl.formatMessage(defaults.hopeValidationPending);
    }
    if (hopeMissingSIA) {
      return intl.formatMessage(defaults.hopeMissingSIA);
    }
    if (hopeMissingDocumentation) {
      return intl.formatMessage(defaults.hopeError, hopeErrorIntlValues);
    }
    return intl.formatMessage(defaults.hopeError, hopeErrorIntlValues);
  };
  const isF2FAllowed = (appVisit && appVisit.isfacetofaceallowed) || false;
  const intervalTimer = 10;
  const MINUTE_MS = 60000 * intervalTimer; // 10 min

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    let numInterval = 1;
    // save the duration every 10 min
    if (savedVisit && patient) {
      const interval = setInterval(() => {
        const closedVisit = {
          ...savedVisit,
          duration: numInterval * intervalTimer,
        };
        /**
         * @note not saving HCPCS as we can't re-build it
         * @note const hcpcs: any = buildHCPCS(...)
         */
        // @note not saving HCPCS as we can't re-build it
        updatePatientVisit(
          (patient as mumms.patient).patientNumber,
          closedVisit,
        );
        ++numInterval;
      }, MINUTE_MS);
      // create time specific title
      const title: any = (
        <FormattedMessage
          {...defaults.openedVisit}
          values={{
            date: appVisit ? getAppVisitText() : null,
          }}
        />
      );
      dispatchVisit({
        type: SET_VISIT_TITLE,
        payload: title,
      });
      return () => clearInterval(interval);
    }
  }, [savedVisit?.id]);

  const getAppVisitText = () => {
    if (savedVisit) {
      const text = IPUVisit ? IPU : savedVisit?.levelofcareDisplayValue;
      return `${text} on ${formatUTCLocalMMDDYY(savedVisit.startDate)}`;
    }
    return '';
  };

  useEffect(() => {
    if (stopVisit) {
      finishVisit();
    }
  }, [stopVisit]);

  // Remove f2f warning when value selected
  useEffect(() => {
    if (savedVisit) {
      const f2fBlank = isF2FAllowed && editFaceToFace === null;
      setWarnF2F(f2fBlank);
    }
  }, [savedVisit, savedVisit?.startDate, isF2FAllowed, editFaceToFace]);

  /** Clinician allowed to use HCPCS on this visit type (API permission). */
  const showHCPCSPermission =
    (!IPUVisit && hcpcsPermission && hcpcsPermission.ishcpcsallowed) || false;

  /** Intro date row: hide for HOPE or face-to-face visits. */
  const showRecordVisitIntroDate = !isHopeVisit && !isF2FAllowed;
  /** Intro role row: hide for HOPE, HCPCS-permission visits, or face-to-face visits. */
  const showRecordVisitIntroRole =
    !isHopeVisit && !showHCPCSPermission && !isF2FAllowed;
  const showRecordVisitIntrType = !isHopeVisit && !showHCPCSPermission;

  const postVisit = () => checkForVisit();

  useEffect(() => {
    if (!disabled) return;
    const stillSubmitting =
      visitClosingRef.current ||
      showHCPCS ||
      showConfirmVisit ||
      stopVisit ||
      showVisitLoader;
    if (!stillSubmitting) {
      setDisabled(false);
    }
  }, [disabled, showHCPCS, showConfirmVisit, stopVisit, showVisitLoader]);

  /**
   * Pre-submit gate: undocumented visit → confirm dialog; otherwise close immediately.
   * Overlapping calls are dropped (ref) so one path cannot open the dialog while another
   * calls finishVisit. visitClosingRef / showConfirmVisit cover close-in-progress and
   * dialog-already-open.
   */
  const checkForVisit = async () => {
    if (
      checkForVisitInFlightRef.current ||
      visitClosingRef.current ||
      showConfirmVisit
    ) {
      return;
    }
    checkForVisitInFlightRef.current = true;
    try {
      if (savedVisit && savedVisit.id && savedVisit.startDate) {
        dispatchHCPCS({
          type: SHOW_VISIT_LOADER,
          payload: true,
        });
        const missingDoc = await canDeleteOpenVisit();
        const hasSavedHCPCS =
          visitHasSavedHCPCS(savedVisit) || visitHasSavedHCPCS(appVisit as any);
        const missingHCPCS = showHCPCSPermission && !hasSavedHCPCS;
        if (missingDoc || missingHCPCS || (isHopeVisit && visitRequiresSFV)) {
          dispatchHCPCS({
            type: SHOW_VISIT_LOADER,
            payload: false,
          });
          dispatchVisit({
            type: SHOW_CONFIRM_VISIT,
            payload: {
              open: true,
              missingDoc,
              missingHCPCS,
            },
          });
        } else {
          finishVisit();
        }
      } else {
        dispatchVisit({
          type: SET_VISIT_ALERT,
          payload: {
            text: defaults.visitDupError,
            severity: 'error',
          },
        });
      }
    } finally {
      checkForVisitInFlightRef.current = false;
    }
  };

  const resetWarnings = () => {
    setWarnF2F(false);
  };
  /** Last step before closing: ask for the duration (and CC hours) in DurationPrompt. */
  const finishVisit = () => {
    if (visitClosingRef.current) {
      return;
    }
    visitClosingRef.current = true;
    dispatchVisit({
      type: SHOW_CONFIRM_VISIT,
      payload: false,
    });
    dispatchVisit({
      type: STOP_VISIT,
      payload: false,
    });
    dispatchHCPCS({
      type: SHOW_VISIT_LOADER,
      payload: false,
    });
    setShowDurationPrompt(true);
  };
  const handleDurationCancel = () => {
    setShowDurationPrompt(false);
    visitClosingRef.current = false;
  };
  const handleDurationSubmit = (duration: number, ccMins: number) => {
    setShowDurationPrompt(false);
    finishCurrentVisit(duration, ccMins);
  };
  /** CC hours entered on the widget; shown in DurationPrompt so they can be fixed there. */
  const ccOnVisit: HhMm | null =
    ccTime && toMinutes([ccTime[0], ccTime[1]]) > 0
      ? [ccTime[0], ccTime[1]]
      : null;
  const finishCurrentVisit = async (duration: number, ccMins: number) => {
    // avoid POST instead of PUT for duplicate records
    if (savedVisit && savedVisit.id && savedVisit.startDate) {
      if (isHopeVisit) {
        if (isOtherInProgHopeVisit) {
          visitClosingRef.current = false;
          dispatchVisit({
            type: SET_VISIT_ALERT,
            payload: {
              text: defaults.hopeVisitWarning,
              severity: 'error',
            },
          });
          return;
        }
        if (!hopeVisitAttributesSaved) {
          visitClosingRef.current = false;
          dispatchVisit({
            type: SET_VISIT_ALERT,
            payload: {
              text: defaults.hopeVisitSaveError,
              severity: 'error',
            },
          });
          return;
        }
        if (canSubmitHopeVisit !== true) {
          visitClosingRef.current = false;
          setDisabled(false);
          dispatchVisit({
            type: SET_VISIT_ALERT,
            payload: {
              text: getHopeSubmitAlertText(),
              severity: 'error',
            },
          });
          return;
        }
      }
      setDisabled(true);

      dispatchHCPCS({
        type: SHOW_VISIT_LOADER,
        payload: true,
      });
      setDisabled(true);
      dispatchVisit({
        type: GET_TIME,
        payload: true,
      });
      resetWarnings();

      const continuouscarehours = ccMins;

      const departCoords = await getUsersGeolocation().catch(
        (error: GeolocationHandlerError) => handleGeolocationError(error),
      );

      // payload does not have endDate => 400 error
      if (savedVisit && savedVisit.endDate) {
        delete savedVisit.endDate;
      }

      if (duration) {
        // SFVRQ is saved on SIA OK; re-derive from final SIA scores so the completed visit is always right.
        if (isHopeVisit === true && visitAttributes) {
          const milestone = getCurrentMilestone(availableHOPEActions) || '';
          if (!milestone.includes('-SFV')) {
            updateVisitSFVRequired(
              visitAttributes,
              siaRequiresSfv(SIAData, milestone),
            );
          }
        }
        const closedVisit: any = {
          ...savedVisit,
          status: Completed,
          tolls: tollsFee,
          transitFees,
          travelMileage,
          travelDuration,
          duration,
          continuouscarehours,
          visitAttributes,
          // pim currently does not accept this value on the api call.
          // isNurseNotified,
        };
        let hopeStatusUpdateDone = false;
        if (isHopeVisit && canSubmitHopeVisit) {
          const hopeSequence = await runHopeRecordVisitSequence({
            ...savedVisit,
            patientnumber: patient?.patientNumber,
          });
          if (!hopeSequence.proceed) {
            visitClosingRef.current = false;
            dispatchHCPCS({
              type: SHOW_VISIT_LOADER,
              payload: false,
            });
            setDisabled(false);
            setHopeValidationModal({
              blockingErrors: hopeSequence.blockingErrors || [],
              hopeDTOProp: hopeSequence.hopeDTOProp || '',
            });
            return;
          }
          hopeStatusUpdateDone = !!hopeSequence.hopeStatusUpdateDone;
        }
        if (editFaceToFace != null) {
          closedVisit.faceToFaceVisit = editFaceToFace;
        }

        if (
          departCoords &&
          departCoords.coords &&
          departCoords.coords.longitude &&
          departCoords.coords.latitude
        ) {
          closedVisit.departLat =
            departCoords.coords.latitude.toString() || null;
          closedVisit.departLon =
            departCoords.coords.longitude.toString() || null;
        }

        if (patient) {
          const visitResponse: any = await closePatientVisit(
            (patient as mumms.patient).patientNumber,
            closedVisit,
          );
          if (visitResponse instanceof Response || visitResponse == null) {
            visitClosingRef.current = false;
            setDisabled(false);
            dispatchHCPCS({
              type: SHOW_VISIT_LOADER,
              payload: false,
            });
            return;
          }
          if (isHopeVisit) {
            if (hopeStatusUpdateDone) {
              refreshHopeData();
            } else {
              const currentMilestone = getCurrentMilestone(
                availableHOPEActions,
              );
              await updateHopeVisitStatus(
                patient.id,
                patient.episodes.length - 1,
                currentMilestone,
                visitResponse,
              );
            }
          }
          if (isHopeVisit && canSubmitHopeVisit) {
            dispatchVG({ type: SET_REFRESH_VISIT });
          }
          dispatchHCPCS({
            type: RESET_HCPCS,
          });
          dispatchHCPCS({
            type: SHOW_VISIT_LOADER,
            payload: false,
          });
        }

        dispatchVisit({
          type: SET_VISIT_TITLE,
          payload: defaults.submittingVisit, // reset the title on widget
        });
      }
      visitClosingRef.current = false;
    } else {
      visitClosingRef.current = false;
      dispatchVisit({
        type: SET_VISIT_ALERT,
        payload: { text: defaults.visitDupError, severity: 'error' },
      });
    }
  };

  const handleNurseNotifedCBChange = () => {
    setNurseNotified(!isNurseNotified);
  };

  // const [pimreaderVisitDTO, setPimreaderVisitDTO] = useState<any>(null);

  const applyAppVisitDetails = (res: any) => {
    if (!res) return;
    dispatchVG({
      type: SET_PROVIDER_INFO,
      payload: { ...providerInfo, role_id: res.role_id },
    });
    dispatchGlobal({ type: SET_APP_VISIT_DETAILS, payload: res });
  };

  const clearReopenVisitDashboardLoader = () => {
    dispatchHCPCS({
      type: SHOW_VISIT_LOADER,
      payload: false,
    });
    dispatchVisit({
      type: SET_OPENED_FROM_VISIT_LOG_REOPEN,
      payload: false,
    });
  };

  const reopenLoaderActiveRef = useRef(openedFromVisitLogReopen);
  reopenLoaderActiveRef.current = openedFromVisitLogReopen;

  useEffect(
    () => () => {
      if (reopenLoaderActiveRef.current) {
        dispatchHCPCS({
          type: SHOW_VISIT_LOADER,
          payload: false,
        });
        dispatchVisit({
          type: SET_OPENED_FROM_VISIT_LOG_REOPEN,
          payload: false,
        });
      }
    },
    [dispatchHCPCS, dispatchVisit],
  );

  const fetchPriorDetails = async () => {
    const priorDTO = appVisit;
    const shouldClearReopenLoader = openedFromVisitLogReopen;
    try {
      if (!priorDTO?.id || !keycloak) return;

      const visitPersonNumber = priorDTO.personnumber ?? null;
      const fetchPimreaderRowForPerson = async (
        personNumber: string,
      ): Promise<any | null> => {
        try {
          const details = await getVisitByPersonAndVisitId(
            personNumber,
            `${priorDTO.id}`,
            agency,
            keycloak,
          );
          return details?.[0] ?? null;
        } catch {
          return null;
        }
      };

      let res: any | null = null;
      if (currentUser.personNumber) {
        res = await fetchPimreaderRowForPerson(currentUser.personNumber);
      }
      if (
        !res &&
        visitPersonNumber &&
        visitPersonNumber !== currentUser.personNumber
      ) {
        res = await fetchPimreaderRowForPerson(visitPersonNumber);
      }

      if (res) {
        applyAppVisitDetails(res);
        return;
      }

      if (!appVisitDetails && priorDTO) {
        applyAppVisitDetails(priorDTO);
      }
    } finally {
      if (shouldClearReopenLoader) {
        clearReopenVisitDashboardLoader();
      }
    }
  };

  useEffect(() => {
    if (!appVisit?.id) {
      return;
    }
    // Why the retriggers
    //
    // The deps are meant to refresh that summary when related chart activity changes:
    //
    // Dep                         Intended reason
    // appVisit / savedVisit       Visit opened or visit object changed
    // progressNotesGlobal?.length Notes added → refresh notes counts
    // numberForms                 Forms changed → refresh forms
    // patientAllCPPTasks?.length  Tasks changed
    // reloadMesures               Measures changed
    //
    // (Implemented as appVisit?.id / savedVisit?.id so EAO-only object patches
    // do not refetch.)
    fetchPriorDetails();
  }, [
    appVisit?.id,
    savedVisit?.id,
    progressNotesGlobal?.length,
    numberForms,
    patientAllCPPTasks?.length,
    reloadMesures,
  ]);

  /**
   * Intro date-picker value: `savedVisit.startDate` in site TZ, else VisitIntro-style maxDate fallback.
   */
  const pickerSelectedDate = () => {
    if (savedVisit?.startDate) {
      return recordSummaryVisitDate;
    }
    const time = moment.tz(maxDate, moment.tz.guess()).tz(siteTimezone);
    return momentToDate(time);
  };

  /** Patches `startDate` on the in-memory visit only; PIM gets it on Submit Visit (`closePatientVisit`). */
  const applyIntroStartDateFromMoment = (start: moment.Moment) => {
    if (!savedVisit) return;
    dispatchVisit({
      type: SET_SAVED_VISIT,
      payload: {
        ...savedVisit,
        startDate: formatPimDate(start),
      },
    });
  };

  /**
   * Attempted visits use date+time in one control (full replacement). Routine/IPU date-only pickers
   * change the calendar day only and keep the existing time-of-day from `savedVisit.startDate`.
   */
  const handleIntroDateChange = (visitdate: unknown) => {
    if (!visitdate || !savedVisit) return;
    if (isDateTimePicker) {
      applyIntroStartDateFromMoment(
        dateToMoment(moment(visitdate as Date).toDate()),
      );
      return;
    }
    const prevStart = utcToTz(savedVisit.startDate, siteTimezone);
    const prevM = moment(prevStart);
    const picked = moment(visitdate as Date);
    const merged = prevM
      .clone()
      .year(picked.year())
      .month(picked.month())
      .date(picked.date());
    applyIntroStartDateFromMoment(dateToMoment(merged.toDate()));
  };

  /** Matches VisitIntro role selection; visit `careProviderRole*` fields apply on Submit Visit only. */
  const onIntroRoleChange = (role: any) => {
    if (!savedVisit) return;
    dispatchVisit({
      type: SET_ACTING_AS_ROLE,
      payload: role.rolename,
    });
    dispatchVisit({
      type: SET_SAVED_VISIT,
      payload: {
        ...savedVisit,
        careProviderRole:
          role.roleshortname != null ? String(role.roleshortname) : null,
        careProviderRoleGroup: role.label != null ? String(role.label) : null,
      },
    });
  };

  /** Same field mapping as StartVisit DTO; persisted to PIM on Submit Visit only. */
  const onIntroLocationTypeChange = (location: any) => {
    if (!savedVisit) return;
    dispatchVisit({
      type: SET_SAVED_VISIT,
      payload: {
        ...savedVisit,
        visitType: location.value,
        displayvisittype: location.value,
      },
    });
  };

  /**
   * VisitIntro-equivalent controls while recording. Edits patch `savedVisit` only (submit persists to PIM).
   * Attempted = datetime only (Start stays below). Otherwise a grid, two pairs per row:
   * Role | Type, Date | Start (IPU: Date | Start).
   * Date: hidden for HOPE or F2F (`isfacetofaceallowed`). Role: hidden when HCPCS API permission applies.
   */
  const renderRecordVisitIntroFields = (): React.ReactNode => {
    if (isDateTimePicker) {
      if (!showRecordVisitIntroDate) {
        return null;
      }
      return (
        <Fragment>
          <VisitInterfaceDiv>
            <TextDiv>{defaults.recordVisitIntroLabelDate}</TextDiv>
            <ReactDateTimePicker
              inputMargin="0 0 4px 0"
              dateFormat="MM-dd-yy h:mm aa"
              inputWidth="125px"
              selectedDate={pickerSelectedDate()}
              onChange={handleIntroDateChange}
              minimumDate={minDate && (minDate as any).toDate()}
              maximumDate={maxDate}
              timeIntervals={is30MinAgency ? 30 : 15}
            />
          </VisitInterfaceDiv>
        </Fragment>
      );
    }
    return (
      <RecordVisitIntroGrid id="record-visit-intro-grid">
        {!IPUVisit && showRecordVisitIntroRole && (
          <>
            <TextDiv>{defaults.recordVisitIntroLabelRole}</TextDiv>
            <SmallDiv align="left" width={190}>
              <SuggestBox
                fitcontent
                // disabled={!(roleGroups && roleGroups.length > 1)}
                options={roleGroups || []}
                value={recordSummaryRoleOption}
                onChange={onIntroRoleChange}
                className={classes.inputBase}
              />
            </SmallDiv>
          </>
        )}
        {!IPUVisit && showRecordVisitIntrType && (
          <>
            <TextDiv>{defaults.recordVisitIntroLabelType}</TextDiv>
            <SmallDiv align="left" width={180}>
              <SuggestBox
                fitcontent
                options={visitTypeOptions || []}
                value={recordSummaryVisitTypeOption}
                onChange={onIntroLocationTypeChange}
                className={classes.inputBase}
              />
            </SmallDiv>
          </>
        )}
        {showRecordVisitIntroDate && (
          <>
            <TextDiv>{defaults.recordVisitIntroLabelDate}</TextDiv>
            <ReactDatePicker
              dateFormat="MM-dd-yy"
              inputMargin="0 0 4px 0"
              inputWidth="125px"
              selectedDate={pickerSelectedDate()}
              minimumDate={minDate && (minDate as any).toDate()}
              maximumDate={maxDate}
              // OP-33239: an IPU visit date stays pinned to its start date; read-only while recording too.
              onChange={IPUVisit ? () => undefined : handleIntroDateChange}
              disabled={IPUVisit}
              readOnly={IPUVisit}
              timeIntervals={is30MinAgency ? 30 : 15}
            />
          </>
        )}
        <EditTime inline />
      </RecordVisitIntroGrid>
    );
  };

  return (
    <>
      {hopeValidationModal && (
        <HopeValidationModal
          open
          hopeDTOProp={hopeValidationModal.hopeDTOProp}
          blockingErrors={hopeValidationModal.blockingErrors}
          onClose={() => setHopeValidationModal(null)}
        />
      )}
      <BusyOverlay busy={!!showVisitLoader && !openedFromVisitLogReopen}>
        {/* Editable visit metadata; PATCH to PIM runs on Submit Visit, not on each change. */}
        <div className="record-visit-intro-fields">
          {renderRecordVisitIntroFields()}
        </div>
        {(isDateTimePicker || !IPUVisit) && (
          <RecordVisitDiv className="record-visit">
            {isDateTimePicker && <EditTime />}
            {!IPUVisit && <Fees />}
          </RecordVisitDiv>
        )}

        {isF2FAllowed && <FaceToFace />}
        <>
          <NurseNotificationCheckbox
            isChecked={isNurseNotified}
            updateNurseNotified={handleNurseNotifedCBChange}
          />
          {hopeMissingSIA && (
            <VisitWarning>
              <span>
                <FormattedMessage {...defaults.hopeMissingSIA} />
              </span>
            </VisitWarning>
          )}
          {hopeMissingDocumentation && (
            <VisitWarning>
              <span>
                <FormattedMessage
                  {...defaults.hopeError}
                  values={hopeErrorIntlValues}
                />
              </span>
            </VisitWarning>
          )}
          {isHopeVisit && isOtherInProgHopeVisit && (
            <VisitWarning>
              <span>{defaults.hopeVisitWarning}</span>
            </VisitWarning>
          )}
          {warnF2F && (
            <VisitWarning>
              <span>{defaults.f2fWarning}</span>
            </VisitWarning>
          )}
          <RecordVisitSummaryLayout>
            <RecordVisitSummaryColumn>
              <VisitContents
                dto={visitContentsDto}
                editing={false}
                editF2F={() => (
                  <Checkbox checked onChange={() => null} disabled />
                )}
                summary
              />
            </RecordVisitSummaryColumn>

            <CloseVisitButtonsDiv>
              <SubmitVisitButton id="submit-visit">
                <DeleteVisitBtn disabled={disabled} />
                <Button
                  title={defaults.submitVisit}
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={postVisit}
                  disabled={
                    visitError || disabled || warnF2F || hopeSubmitBlocked
                  }
                  classes={{
                    root: classes.button,
                  }}
                >
                  {defaults.submitVisit}
                </Button>
              </SubmitVisitButton>
            </CloseVisitButtonsDiv>
          </RecordVisitSummaryLayout>
        </>
      </BusyOverlay>
      <ConfirmVisitDialog />
      {showDurationPrompt && (
        <DurationPrompt
          initialDuration={getInitialDuration(
            savedVisit?.startDate,
            moment(),
            getDurationMinutesFromVisitSources(savedVisit, appVisit),
          )}
          initialCC={ccOnVisit}
          onCancel={handleDurationCancel}
          onSubmit={handleDurationSubmit}
        />
      )}
    </>
  );
};
export default RecordVisit;
